import {createContext, ReactNode, useCallback, useContext, useMemo, useState} from "react";

import {AuthContextModel, AuthInfo} from "../types/contexts/AuthContext";
import {getFromToken, JwtKeys} from "../utils/jwtUtils";

const AuthContext = createContext<AuthContextModel | undefined>(undefined);

const getStoredAuthInfo = (): AuthInfo | null => {
    try {
        const stored = localStorage.getItem("authInfo");
        return stored ? JSON.parse(stored) : null;
    } catch {
        return null;
    }
};

export const AuthProvider = ({children}: { children: ReactNode }) => {

    const [authInfo, setAuthInfo] = useState<AuthInfo | null>(getStoredAuthInfo());

    const saveAuthInfo = useCallback((data: AuthInfo) => {
        setAuthInfo(data);
        localStorage.setItem("authInfo", JSON.stringify(data));
    }, []);

    const clearAuthInfo = useCallback(() => {
        setAuthInfo(null);
        localStorage.removeItem("authInfo");
    }, []);

    const value = useMemo(() => ({
        authInfo,
        saveAuthInfo,
        clearAuthInfo,
        isAuthenticated: !!authInfo,
        authenticatedUserId: authInfo?.accessToken
            ? getFromToken(authInfo.accessToken, JwtKeys.SUB)
            : null,
    }), [authInfo, saveAuthInfo, clearAuthInfo]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
